// IMPORTANT: keys live directly under the file's namespace, not wrapped in an
// outer `team:` object. The runtime loader (i18n/local/index.ts) already uses
// the filename as the namespace, so wrapping caused every `t('title')` call
// inside `useTranslation('team')` to resolve to `team.team.title` and fall
// back to the English default. If you add new keys, put them at the top
// level — only the genuine subtrees (`acceptInvitation.*`) stay nested.
export default {
  title: 'Team',
  members: 'Members',
  pendingInvitations: 'Pending Invitations',
  inviteMember: 'Invite Member',
  inviteAnother: 'Invite Another',
  // Header / page actions
  backToDashboard: 'Dashboard',
  inviteButton: 'Invite',
  teamUsage: 'Team Members',
  freeTier: 'free tier',
  subscriptionRequired: 'Subscribe to invite more team members',
  limitReached: 'Team member limit reached. Upgrade your plan.',
  upgradeToInviteMore: 'Subscribe to invite more members →',
  resendFailed: 'Failed to resend invitation',
  cancelFailed: 'Failed to cancel invitation',
  invitationResent: 'Invitation resent to {{email}}',
  invitationCancelled: 'Invitation cancelled',
  membersCount_one: '{{count}} member',
  membersCount_other: '{{count}} members',
  // Pending invitation row (PendingInvitationRow).
  sent: 'Sent',
  cannotResendExpired: 'Cannot resend expired invitation',
  // Member display
  memberSince: 'Member since',
  listings: 'Listings',
  you: 'You',
  // Role names
  roleOwner: 'Owner',
  roleAdmin: 'Admin',
  roleAgent: 'Agent',
  roleViewer: 'Viewer',
  // Role descriptions for invite form
  roleAdminDesc: 'Can manage team members and company settings',
  roleAgentDesc: 'Can create and manage property listings',
  roleViewerDesc: 'Can view team and properties (read-only)',
  // Actions
  changeRole: 'Change Role',
  removeMember: 'Remove Member',
  resend: 'Resend',
  cancel: 'Cancel',
  copyLink: 'Copy Link',
  // Dialogs
  currentRole: 'Current role:',
  newRole: 'New role',
  changeRoleTitle: 'Change Role',
  changeRoleConfirm: "Change {{name}}'s role to {{role}}?",
  removeMemberTitle: 'Remove Team Member',
  removeMemberWarning:
    'Removing {{name}} will revoke their access to company properties and team features.',
  removeMemberConfirmTitle: 'Confirm Removal',
  removeMemberConfirmPrompt: 'Type "{{name}}" to confirm removal:',
  ownerProtection: "You must transfer ownership before changing the owner's role.",
  // Toast messages
  inviteSent: 'Invitation sent to {{email}}',
  inviteResent: 'Invitation resent to {{email}}',
  inviteCancelled: 'Invitation cancelled',
  roleChanged: "{{name}}'s role changed to {{role}}",
  memberRemoved: '{{name}} has been removed from the team',
  memberRemovedWithTransfer: '{{name}} removed · {{count}} listings transferred to the company',
  // Empty states
  noMembers: 'No team members yet',
  buildYourTeam: 'Build your team',
  buildYourTeamDesc: 'Invite colleagues to help manage your properties',
  noPendingInvitations: 'No pending invitations',
  // Errors
  inviteFailed: 'Failed to send invitation',
  alreadyMember: 'This person is already a team member',
  alreadyInvited: 'This person already has a pending invitation',
  // Invite form
  emailLabel: 'Email Address',
  emailPlaceholder: 'colleague@company.com',
  selectRole: 'Select Role',
  sendInvitation: 'Send Invitation',
  // Accept invitation page
  acceptInvitation: {
    verifying: 'Verifying invitation...',
    title: "You've been invited to join a team",
    email: 'Email',
    role: 'Role',
    invitedBy: 'Invited by',
    acceptButton: 'Accept Invitation',
    accepting: 'Accepting...',
    acceptDisclaimer: 'By accepting, you\'ll become a {{role}} at {{company}}',
    successTitle: 'Welcome to {{company}}!',
    successMessage: "You're now a {{role}} on the team.",
    goToTeam: 'Go to Team Page',
    expiredTitle: 'Invitation Expired',
    expiredMessage: 'This invitation has expired. Please ask the company admin to send a new invitation.',
    invalidTitle: 'Invalid Invitation',
    invalidMessage: 'This invitation link is invalid or has already been used.',
    goHome: 'Go to Home',
    signInFirst: 'Sign in to accept this invitation',
    signInButton: 'Sign In',
    alreadyMember: "You're already a member of this company",
    alreadyMemberTitle: 'Already a Member',
    errorTitle: 'Something Went Wrong',
    genericError: 'Failed to accept invitation. Please try again.',
    tryAgain: 'Try Again',
    createAccount: 'Create an account to join the team',
    firstName: 'First Name',
    lastName: 'Last Name',
    firstNamePlaceholder: 'Enter your first name',
    lastNamePlaceholder: 'Enter your last name',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    passwordPlaceholder: 'Enter a password',
    confirmPasswordPlaceholder: 'Re-enter your password',
    createAndJoin: 'Create Account & Join',
    transferListings: 'Transfer my listings to this company',
    transferListingsDesc: 'Your {{count}} listings will be associated with {{company}}',
    differentUser: 'You are logged in as {{email}}, but this invitation is for a different email.',
    switchAccount: 'Sign out and use the correct account',
    validation: {
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      passwordRequired: 'Password is required',
      passwordTooShort: 'Password must be at least 8 characters',
      passwordMismatch: 'Passwords do not match',
    },
  },
};
